import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Award, CheckCircle, ArrowRight, BookOpen, FileText, Clock } from 'lucide-react';

export default function CertificationPage() {
  return (
    <div data-testid="certification-page">
      <section className="bg-[#1B2A4A] py-16 md:py-20">
        <div className="imcac-container">
          <p className="imcac-overline mb-3">Board Certification</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight font-heading">CBEAM Board Certification</h1>
          <div className="h-1 w-20 bg-[#C5A572] mt-5" />
        </div>
      </section>

      <section className="imcac-section bg-white">
        <div className="imcac-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7">
              <h2 className="text-3xl font-semibold text-[#1B2A4A] font-heading mb-6">
                Competency-Based Examination in Aesthetic Medicine
              </h2>
              <p className="text-base text-slate-600 leading-relaxed mb-4">
                The CBEAM (Competency-Based Examination in Aesthetic Medicine) represents the gold standard in 
                post-licensure aesthetic medicine certification. Administered by IMCAC's Board of Competency 
                Certification, CBEAM validates clinical competence through rigorous, multi-modal assessment.
              </p>

              <div className="space-y-4 mb-8">
                <h3 className="text-xl font-semibold text-[#1B2A4A] font-heading">Certification Titles</h3>
                <Card className="imcac-card border-l-4 border-l-[#C5A572]">
                  <CardContent className="p-5">
                    <Badge className="bg-[#C5A572] text-white mb-2">Medical Doctors</Badge>
                    <p className="text-lg font-semibold text-[#1B2A4A] font-heading">Board Certified in [Specialty] (IMCAC – CBEXX)</p>
                    <p className="text-sm text-slate-500 mt-1">e.g., Board Certified in Aesthetic Medicine (IMCAC – CBEAM)</p>
                  </CardContent>
                </Card>
                <Card className="imcac-card border-l-4 border-l-[#1B2A4A]">
                  <CardContent className="p-5">
                    <Badge className="bg-[#1B2A4A] text-white mb-2">Dental Practitioners</Badge>
                    <p className="text-lg font-semibold text-[#1B2A4A] font-heading">Board Certified in Aesthetic Dentistry & Facial Aesthetics (IMCAC – CBEAD)</p>
                  </CardContent>
                </Card>
                <Card className="imcac-card border-l-4 border-l-[#22c55e]">
                  <CardContent className="p-5">
                    <Badge className="bg-[#22c55e] text-white mb-2">Registered Nurses</Badge>
                    <p className="text-lg font-semibold text-[#1B2A4A] font-heading">Board Certified in Aesthetic Clinical Nursing (IMCAC – CBENA)</p>
                  </CardContent>
                </Card>
                <Card className="imcac-card border-l-4 border-l-[#6366f1]">
                  <CardContent className="p-5">
                    <Badge className="bg-[#6366f1] text-white mb-2">Clinical Assistants</Badge>
                    <p className="text-lg font-semibold text-[#1B2A4A] font-heading">Certified in Clinical Aesthetic Assistance (IMCAC – CBEAA)</p>
                  </CardContent>
                </Card>
              </div>

              <h3 className="text-xl font-semibold text-[#1B2A4A] font-heading mb-4">Assessment Components</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                {[
                  { icon: FileText, title: 'Written Examination', desc: 'MCQ-based assessment across 10 clinical categories' },
                  { icon: BookOpen, title: 'Full OSCE Assessment', desc: '10-12 clinical stations with standardised marking' },
                  { icon: Clock, title: 'Case Log Verification', desc: 'Review of documented clinical cases and outcomes' },
                  { icon: Award, title: 'Viva & Case Defense', desc: 'Oral examination and clinical reasoning assessment' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3 p-4 bg-[#F8FAFC] rounded-sm border border-slate-100">
                    <item.icon className="h-5 w-5 text-[#C5A572] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-[#1B2A4A]">{item.title}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-4">
                <Link to="/apply" data-testid="cert-apply-btn">
                  <Button className="bg-[#1B2A4A] hover:bg-[#152039] text-white rounded-sm">
                    Apply for Certification <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/examination-pathways" data-testid="cert-pathways-btn">
                  <Button variant="outline" className="rounded-sm border-[#1B2A4A] text-[#1B2A4A]">View Pathways</Button>
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5">
              <img
                src="https://customer-assets.emergentagent.com/job_cbeam-portal/artifacts/6rd2j9th_Laptop%20View%20of%20Verification%20page%20%286%29.png"
                alt="IMCAC Certificate of Competency"
                className="w-full h-72 object-cover rounded-sm shadow-md mb-6"
              />
              <Card className="imcac-card">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-[#1B2A4A] font-heading mb-4">Certification Validity</h3>
                  <ul className="space-y-3 text-sm text-slate-700">
                    <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-[#C5A572] flex-shrink-0 mt-0.5" />Initial certification valid for 5 years</li>
                    <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-[#C5A572] flex-shrink-0 mt-0.5" />Recertification through CPD compliance</li>
                    <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-[#C5A572] flex-shrink-0 mt-0.5" />Online verification available to public</li>
                    <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-[#C5A572] flex-shrink-0 mt-0.5" />QR code verification on certificates</li>
                    <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-[#C5A572] flex-shrink-0 mt-0.5" />Revocable for non-compliance</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
